import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Smartphone, Apple } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin-UI für den In-App-Update-Check der Kunden-App.
 * Setzt je Plattform die "neueste" Version (Soft-Update, empfohlen) und die
 * "Mindest"-Version (Force-Update, erzwungen) + Store-URL.
 * Persistenz via PUT /api/admin/mobile-app/version-config.
 */
function AppVersionConfig() {
  const token = () => localStorage.getItem('token');
  const [cfg, setCfg] = useState({
    enabled: false,
    ios_latest_version: '',
    ios_min_version: '',
    ios_store_url: '',
    android_latest_version: '',
    android_min_version: '',
    android_store_url: '',
    message_de: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/mobile-app/version-config`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        if (res.data?.config) setCfg((c) => ({ ...c, ...res.data.config }));
      } catch (e) {
        toast.error('Fehler beim Laden der App-Version-Konfiguration');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/mobile-app/version-config`, cfg, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      toast.success('App-Version-Konfiguration gespeichert');
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-500" data-testid="app-version-config-loading">Lade App-Version-Konfiguration…</div>;
  }

  const platformBlock = (title, Icon, prefix) => (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-gray-700" />
        <p className="font-semibold text-gray-900">{title}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs text-gray-500">Neueste Version (empfohlen)</Label>
          <Input
            placeholder="z.B. 1.0.19"
            value={cfg[`${prefix}_latest_version`] || ''}
            onChange={(e) => set(`${prefix}_latest_version`, e.target.value)}
            data-testid={`input-${prefix}-latest`}
          />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Mindest-Version (erzwingen)</Label>
          <Input
            placeholder="z.B. 1.0.18"
            value={cfg[`${prefix}_min_version`] || ''}
            onChange={(e) => set(`${prefix}_min_version`, e.target.value)}
            data-testid={`input-${prefix}-min`}
          />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Store-Link</Label>
          <Input
            placeholder="https://…"
            value={cfg[`${prefix}_store_url`] || ''}
            onChange={(e) => set(`${prefix}_store_url`, e.target.value)}
            data-testid={`input-${prefix}-store`}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="app-version-config">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">In-App-Update-Hinweis</h3>
          <p className="text-sm text-gray-500 mt-1">
            Zeigt Kunden in der App einen „Update verfügbar"-Hinweis. Liegt die installierte
            Version unter der <b>Mindest-Version</b>, wird das Update <b>erzwungen</b> (nicht schließbar);
            unter der <b>neuesten Version</b> nur <b>empfohlen</b> (schließbar). Leer lassen = kein Hinweis.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Label className="text-sm text-gray-700">Aktiv</Label>
          <Switch
            checked={!!cfg.enabled}
            onCheckedChange={(v) => set('enabled', v)}
            data-testid="toggle-version-check-enabled"
          />
        </div>
      </div>

      {platformBlock('iOS (App Store)', Apple, 'ios')}
      {platformBlock('Android (Play Store)', Smartphone, 'android')}

      <div>
        <Label className="text-xs text-gray-500">Hinweistext (optional, überschreibt Standard)</Label>
        <Input
          placeholder="z.B. Wichtiges Update mit Fehlerbehebungen – bitte aktualisieren."
          value={cfg.message_de || ''}
          onChange={(e) => set('message_de', e.target.value)}
          data-testid="input-version-message"
        />
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-version-config-btn">
          {saving ? 'Speichern…' : 'Speichern'}
        </Button>
      </div>
    </div>
  );
}

export default AppVersionConfig;
